import React, { useState, useEffect, useRef } from "react";
import { slides } from "components/ui/slider/slides";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { PiVideoLight } from "react-icons/pi";
import VideoModal from "../Videos"; // Import your VideoModal component

// Create an infinite loop array by duplicating slides
const createInfiniteSlides = (items, slidesToShow) => {
  if (items.length <= slidesToShow) return items;
  const lastSlides = items.slice(-slidesToShow);
  const firstSlides = items.slice(0, slidesToShow);
  return [...lastSlides, ...items, ...firstSlides];
};

const videos = [
  "https://firebasestorage.googleapis.com/v0/b/donation-mitq.firebasestorage.app/o/video1.mp4?alt=media&token=5f343d79-23e5-4f13-94a1-ea806e5507e4",
  "https://firebasestorage.googleapis.com/v0/b/donation-mitq.firebasestorage.app/o/video2.mp4?alt=media&token=7c1e4c50-a115-4662-a150-91425ca13d81",
  "https://firebasestorage.googleapis.com/v0/b/donation-mitq.firebasestorage.app/o/video3.mp4?alt=media&token=310a0565-374e-479d-b32b-bbbde39a21c3",
  "https://firebasestorage.googleapis.com/v0/b/donation-mitq.firebasestorage.app/o/video4.mp4?alt=media&token=df3a996e-b9f4-426d-8479-41fe47ef422f",
];

const ResponsiveSlider = ({ lang = "hy" }) => {
  const [slidesToShow, setSlidesToShow] = useState(3);
  const [infiniteSlides, setInfiniteSlides] = useState([]);
  const [current, setCurrent] = useState(slidesToShow);
  const intervalRef = useRef(null);
  const sliderRef = useRef(null);
  const touchStartX = useRef(null); // Ref to store the starting X coordinate of a touch

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  // 🔑 Swipe Logic Functions
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    stopAutoplay();
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const difference = touchStartX.current - touchEndX;

    // Determine swipe direction based on a threshold (e.g., 50px)
    if (difference > 50) {
      nextSlide();
    } else if (difference < -50) {
      prevSlide();
    }
    startAutoplay();
  };

  useEffect(() => {
    const updateSlides = () => {
      let num = 3;
      if (window.innerWidth < 480) num = 1;
      else if (window.innerWidth < 768) num = 2;
      else if (window.innerWidth < 1024) num = 3;
      setSlidesToShow(num);
      setCurrent(num);
      setInfiniteSlides(createInfiniteSlides(slides[lang], num));
    };

    updateSlides();
    window.addEventListener("resize", updateSlides);
    return () => window.removeEventListener("resize", updateSlides);
  }, [lang]);

  useEffect(() => {
    if (sliderRef.current) {
      if (current === infiniteSlides.length - slidesToShow) {
        setTimeout(() => {
          sliderRef.current.style.transition = "none";
          setCurrent(slidesToShow);
        }, 500);
      }
      if (current === 0) {
        setTimeout(() => {
          sliderRef.current.style.transition = "none";
          setCurrent(infiniteSlides.length - slidesToShow * 2);
        }, 500);
      }
    }
  }, [current, infiniteSlides, slidesToShow]);

  useEffect(() => {
    startAutoplay();
    return stopAutoplay;
  }, [slidesToShow, current]);

  const startAutoplay = () => {
    stopAutoplay();
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => prev + 1);
      sliderRef.current.style.transition = "transform 0.5s ease-in-out";
    }, 3000);
  };

  const stopAutoplay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const nextSlide = () => {
    setCurrent((prev) => prev + 1);
    sliderRef.current.style.transition = "transform 0.5s ease-in-out";
    stopAutoplay();
  };

  const prevSlide = () => {
    setCurrent((prev) => prev - 1);
    sliderRef.current.style.transition = "transform 0.5s ease-in-out";
    stopAutoplay();
  };

  const goToSlide = (pageIndex) => {
    setCurrent(pageIndex * slidesToShow + slidesToShow);
    sliderRef.current.style.transition = "transform 0.5s ease-in-out";
    stopAutoplay();
  };

  const handleCardClick = (i) => {
    const realIndex =
      i >= slidesToShow && i < infiniteSlides.length - slidesToShow
        ? i - slidesToShow
        : i;

    setSelectedVideoIndex(realIndex);
    setIsModalOpen(true);
  };

  return (
    <>
      <div
        className="relative w-full overflow-hidden group"
        onMouseEnter={stopAutoplay}
        onMouseLeave={startAutoplay}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Slides wrapper */}
        <div
          ref={sliderRef}
          className="flex"
          style={{
            transform: `translateX(-${(100 / infiniteSlides.length) * current}%)`,
            width: `${infiniteSlides.length * (100 / slidesToShow)}%`,
            transition: "transform 0.5s ease-in-out",
          }}
        >
          {infiniteSlides.map((item, i) => (
            // Add a click handler to the card
            <div
              key={i}
              className="px-3 cursor-pointer"
              style={{ width: `${100 / infiniteSlides.length}%` }}
              onClick={() => handleCardClick(i)}
            >
              <div className="bg-[#D9A443] hover:bg-[#E8BB68] font-[600] text-[20px] p-6 rounded-2xl h-[220px] flex items-center justify-center text-center relative border-[2px] border-transparent hover:border-[2px] hover:border-black transition-all duration-300">
                {item}
                <div className="absolute top-4 right-4 text-3xl text-black">
                  <PiVideoLight />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute top-1/2 left-4 -translate-y-1/2 p-2 bg-white rounded-full shadow-md text-2xl z-10 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <IoIosArrowBack />
        </button>
        <button
          onClick={nextSlide}
          className="absolute top-1/2 right-4 -translate-y-1/2 p-2 bg-white rounded-full shadow-md text-2xl z-10 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <IoIosArrowForward />
        </button>

        {/* Paging Dots */}
        <div className="flex justify-center mt-4 space-x-2">
          {[...Array(Math.ceil(slides[lang].length / slidesToShow))].map(
            (_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  Math.floor((current - slidesToShow) / slidesToShow) === i
                    ? "bg-black"
                    : "bg-gray-400"
                }`}
              />
            ),
          )}
        </div>
      </div>

      {/* Render the VideoModal */}
      <VideoModal
        videos={videos} // Pass your list of video URLs
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        startIndex={selectedVideoIndex} // Pass the selected index
      />
    </>
  );
};

export default ResponsiveSlider;
